import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_ITEMS } from "@/lib/theme";
import { useSettings } from "@/context/SettingsContext";

export default function TopBar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isHome = pathname === "/" || pathname === "/understanding-yu";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Robust mobile scroll-lock that preserves & restores the exact scroll
  // position (position:fixed works on iOS Safari where overflow:hidden fails).
  // Only ever active on mobile — the drawer cannot open on desktop.
  useEffect(() => {
    if (!open) return undefined;
    const scrollY = window.scrollY;
    const { body } = document;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const transparent = isHome && !scrolled;
  const textColor = transparent ? "text-white" : "text-lux-ink";

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-[100] transition-colors duration-300 ${
          transparent
            ? "bg-lux-ink/15"
            : "bg-lux-bg/85 backdrop-blur-md border-b border-lux-line"
        }`}
      >
        <div className="w-full max-w-[1200px] mx-auto px-6 md:px-12 flex items-center justify-between py-4 md:py-5">
          <Logo to="/" height={26} light={transparent} />
          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {NAV_ITEMS.map((item) => {
              const active = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`topnav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`text-[12px] tracking-[0.18em] uppercase font-medium transition-opacity duration-200 hover:opacity-70 ${textColor} ${active ? "underline underline-offset-4" : ""}`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
          {/* Mobile hamburger */}
          <button
            type="button"
            data-testid="hamburger-button"
            onClick={() => setOpen(true)}
            className={`lg:hidden ${textColor}`}
            aria-label="Open menu"
          >
            <Menu size={28} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* Fullscreen drawer */}
      {open && (
        <div className="fixed inset-0 z-[300] bg-lux-bg flex flex-col" style={{ backgroundColor: "#F7F5F0" }} role="dialog" aria-modal="true" data-testid="mobile-nav-drawer">
          <div className="flex flex-col flex-1 px-6 pt-6 pb-8 animate-[luxFadeUp_0.3s_ease]">
          <div className="flex items-center justify-between mb-12">
            <Logo height={24} />
            <button type="button" data-testid="drawer-close" onClick={() => setOpen(false)} aria-label="Close menu" className="text-lux-ink">
              <X size={28} strokeWidth={1.5} />
            </button>
          </div>
          <nav className="flex flex-col">
            {NAV_ITEMS.map((item, idx) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-baseline gap-6 py-5 border-b border-lux-line"
              >
                <span className="text-[11px] tracking-[0.2em] text-lux-muted w-8">{String(idx + 1).padStart(2, "0")}</span>
                <span className="font-serif text-3xl font-light text-lux-ink tracking-tight">{item.label}</span>
              </Link>
            ))}
          </nav>
          <div className="mt-auto pt-8 flex flex-col gap-3">
            <Link to="/understanding-yu" onClick={() => setOpen(false)} data-testid="drawer-understanding-yu" className="text-[13px] tracking-[0.15em] uppercase text-lux-accent">
              Understanding YÜ
            </Link>
            <a href={settings.socials.instagram_url} target="_blank" rel="noopener noreferrer" className="text-[11px] tracking-[0.2em] uppercase text-lux-muted">
              Instagram · @{settings.socials.instagram_handle}
            </a>
            <Link to="/login" onClick={() => setOpen(false)} data-testid="drawer-admin" className="text-[11px] tracking-[0.2em] uppercase text-lux-muted">
              Studio Access
            </Link>
          </div>
          </div>
        </div>
      )}
    </>
  );
}
